import React, { useEffect, useRef, useState } from 'react';
import './time-input.scss';
import { TypedLocalState } from 'utils/redux-hooks';
import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

interface ITimeInputProps {
    className: string;
    id: string;
    name: string;
    value?: number;
    onChange: (value: string) => void;
    disabled: boolean;
    isValid?: boolean;
}

const formatTime = (input: Dayjs): string => {
    if (!input) {
        return '';
    }

    if (!dayjs(input).isValid()) {
        return '';
    }

    return input.format('HHmm');
};

const TimeInput = ({
    className,
    id,
    name,
    value,
    onChange,
    disabled,
    isValid = true,
}: ITimeInputProps): JSX.Element => {
    const [time, setTime]: TypedLocalState<string> = useState(formatTime(dayjs(value?.toString(), 'HHmm')));
    const [showMistakeIndicator, setShowMistakeIndicator]: TypedLocalState<boolean> = useState(false);
    const timeInput = useRef(null);
    const numericTime = parseInt(time);

    useEffect(() => {
        if (time === '' || time.length < 4 || time.match(/[^0-9]/g) || numericTime > 2359) {
            // the user is not finished inputting the time; do not update
            onChange('');
            return;
        }

        if (dayjs(time, 'HHmm').isValid()) {
            onChange(time);
        } else {
            onChange('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [time]);

    const handleChange = (newValue: string) => {
        setShowMistakeIndicator(false);

        const numericValue = parseInt(newValue);

        if (isNaN(numericValue) && newValue !== '') {
            setShowMistakeIndicator(true);
            return;
        }

        if (!dayjs(newValue, 'HHmm', true).isValid()) {
            setShowMistakeIndicator(true);
        }

        setTime(newValue);
    };

    const onFocus = () => {
        timeInput.current.placeholder = '';
    };

    const validationMessage = showMistakeIndicator ? 'Use 24-hour time' : '';

    return (
        <>
            <span
                className={classNames([
                    'time-input-container',
                    {
                        'mistake-indicator': showMistakeIndicator || !isValid,
                    },
                ])}
                onFocus={onFocus}
            >
                <input
                    ref={timeInput}
                    className={classNames(['time-input', className])}
                    disabled={disabled}
                    value={time}
                    id={id}
                    name={name}
                    // this suppresses a warning in the browser console
                    onChange={() => {
                        // do nothing
                    }}
                    // Typescript doesn't know about onInput, and we had to use
                    // onInput, because onChange fires when the form values are
                    // loaded for the first time, which results in the form always
                    // being "dirty."

                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    //@ts-ignore
                    onInput={(e) => handleChange(e.target.value)}
                    placeholder={'----'}
                />
            </span>
            <div className="validation-message">{validationMessage}</div>
        </>
    );
};

export default TimeInput;
